import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
  Legend,
} from "recharts";
import AnimatedCounter from "@/components/base/AnimatedCounter";
import { useAuth } from "@/contexts/AuthContext";
import { getRoleBadgeColor } from "@/mocks/auth";
import { api } from "@/api/client";
import type { DashboardSummaryResponse, DistrictPredictionDetail } from "@/api/types";


import { useTheme } from "@/contexts/ThemeContext";

function KPICard({
  title,
  value,
  change,
  changeLabel,
  icon,
  color,
  delay,
}: {
  title: string;
  value: number;
  change: number;
  changeLabel: string;
  icon: string;
  color: "teal" | "amber" | "red" | "green";
  delay: number;
}) {
  const colorClasses = {
    teal: { bg: "bg-teal-muted/30", border: "border-teal/15", icon: "text-teal", glow: "shadow-glow" },
    amber: { bg: "bg-amber-muted/30", border: "border-amber/15", icon: "text-amber", glow: "shadow-glow-amber" },
    red: { bg: "bg-red-950/30", border: "border-red-500/15", icon: "text-risk-high", glow: "shadow-glow-red" },
    green: { bg: "bg-green-950/30", border: "border-green-500/15", icon: "text-risk-low", glow: "" },
  };
  const c = colorClasses[color];
  const isPositive = change >= 0;

  return (
    <div
      className={`relative overflow-hidden rounded-xl border ${c.border} bg-white dark:bg-slate-900/80 p-5 animate-slide-up`}
      style={{ animationDelay: `${delay}ms`, animationFillMode: "both" }}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-slate-500 dark:text-slate-400 font-medium uppercase tracking-wider">{title}</p>
          <div className="mt-2">
            <span className="text-2xl font-display font-bold text-slate-900 dark:text-white">
              <AnimatedCounter value={value} />
            </span>
          </div>
          <div className="mt-1.5 flex items-center gap-1.5">
            <span
              className={`text-xs font-mono font-medium ${
                isPositive ? "text-risk-high" : "text-risk-low"
              }`}
            >
              {isPositive ? "+" : ""}
              {change}%
            </span>
            <span className="text-[10px] text-slate-400 dark:text-slate-500">{changeLabel}</span>
          </div>
        </div>
        <div
          className={`w-10 h-10 flex items-center justify-center rounded-lg ${c.bg} ${c.glow}`}
        >
          <i className={`${icon} text-lg ${c.icon}`} />
        </div>
      </div>
    </div>
  );
}

type QuickAlert = {
  id: number;
  district_id: number;
  district_name?: string | null;
  district_region?: string | null;
  risk_level: "high" | "medium" | "low";
  predicted_increase_pct: number;
  status: "active" | "acknowledged" | "resolved";
  recommended_action?: string | null;
  ai_reasoning?: string | null;
  contributing_factors?: string | null;
  created_at: string;
  resolved_at?: string | null;
};

function QuickAlertRow({ alert, delay }: { alert: QuickAlert; delay: number }) {
  const { t } = useTranslation();
  const levelColors: Record<string, { badge: string; dot: string }> = {
    high: { badge: "bg-risk-high/15 text-risk-high border-risk-high/20", dot: "bg-risk-high" },
    medium: { badge: "bg-amber/15 text-amber border-amber/20", dot: "bg-amber" },
    low: { badge: "bg-risk-low/15 text-risk-low border-risk-low/20", dot: "bg-risk-low" },
  };
  const c = levelColors[alert?.risk_level ?? "low"] ?? levelColors.low;

  return (
    <div
      className="flex items-center gap-3 py-3 border-b border-slate-200 dark:border-slate-800 last:border-0 animate-fade-in"
      style={{ animationDelay: `${delay}ms`, animationFillMode: "both" }}
    >
      <span className={`w-2 h-2 rounded-full ${c.dot} flex-shrink-0`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">{alert.district_name}</p>
        <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5 truncate">
          {alert.recommended_action ?? "—"}
        </p>
      </div>
      <span
        className={`px-2 py-0.5 rounded-full text-[10px] font-mono font-medium border ${c.badge} whitespace-nowrap`}
      >
        {alert.risk_level === "high"
          ? t("common_high")
          : alert.risk_level === "medium"
            ? t("common_medium")
            : t("common_low")}
      </span>
    </div>
  );
}

// Records come from DashboardSummaryResponse.weather_snapshot.records
function WeatherMiniChart({
  records,
}: {
  records: DashboardSummaryResponse["weather_snapshot"]["records"];
}) {
  const { theme } = useTheme();

  const data = useMemo(() => {
    return [...records]
      .filter((r) => r.avg_temp_c != null || r.total_rainfall_mm != null)
      .sort((a, b) => a.week_start.localeCompare(b.week_start))
      .map((r) => ({
        date: r.week_start.slice(5), // MM-DD
        temp: r.avg_temp_c ?? 0,
        rain: r.total_rainfall_mm ?? 0,
      }));
  }, [records]);

  const tooltipStyle =
    theme === "dark"
      ? { backgroundColor: "#0f172a", border: "1px solid #334155", borderRadius: "8px", fontSize: "12px", color: "#e2e8f0" }
      : { backgroundColor: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "8px", fontSize: "12px", color: "#1e293b" };
  const gridStroke = theme === "dark" ? "#1e293b" : "#e2e8f0";
  const tickFill = theme === "dark" ? "#64748b" : "#94a3b8";

  if (data.length === 0) {
    return (
      <div className="h-[160px] mt-3 flex items-center justify-center text-xs text-slate-400 dark:text-slate-600">
        No weather data available
      </div>
    );
  }

  return (
    <div className="h-[160px] mt-3">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
          <defs>
            <linearGradient id="tempGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#2dd4bf" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#2dd4bf" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="rainGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
          <XAxis dataKey="date" tick={{ fontSize: 10, fill: tickFill }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 10, fill: tickFill }} axisLine={false} tickLine={false} />
          <Tooltip contentStyle={tooltipStyle} itemStyle={{ fontSize: "12px" }} />
          <Area type="monotone" dataKey="temp" stroke="#2dd4bf" strokeWidth={2} fill="url(#tempGrad)" name="Temp °C" />
          <Area type="monotone" dataKey="rain" stroke="#3b82f6" strokeWidth={2} fill="url(#rainGrad)" name="Rain mm" />
          <Legend iconType="circle" iconSize={6} wrapperStyle={{ fontSize: "10px", color: tickFill }} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

// District risk table — driven entirely by backend district_map data
function DistrictMapCard({
  districtMap,
}: {
  districtMap: DashboardSummaryResponse["district_map"];
}) {
  const { t } = useTranslation();

  const riskCounts = useMemo(() => {
    return districtMap.reduce(
      (acc, d) => {
        if (d.risk_level === "high") acc.high++;
        else if (d.risk_level === "medium") acc.medium++;
        else acc.low++;
        return acc;
      },
      { high: 0, medium: 0, low: 0 }
    );
  }, [districtMap]);

  const sorted = useMemo(() =>
    [...districtMap].sort((a, b) => {
      const order: Record<string, number> = { high: 0, medium: 1, low: 2 };
      return (order[a.risk_level ?? "low"] ?? 2) - (order[b.risk_level ?? "low"] ?? 2);
    }),
  [districtMap]);

  const riskBadge: Record<string, string> = {
    high: "bg-risk-high/15 text-risk-high border-risk-high/20",
    medium: "bg-amber/15 text-amber border-amber/20",
    low: "bg-risk-low/15 text-risk-low border-risk-low/20",
  };
  const riskDot: Record<string, string> = {
    high: "bg-risk-high",
    medium: "bg-amber",
    low: "bg-risk-low",
  };

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white font-display">{t("dash_district_map")}</h3>
          <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">
            {districtMap.length} {t("dash_districts_monitored") ?? "districts monitored"}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-risk-high" />
            <span className="text-[10px] text-slate-500 dark:text-slate-400">{riskCounts.high} {t("common_high")}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-risk-medium" />
            <span className="text-[10px] text-slate-500 dark:text-slate-400">{riskCounts.medium} {t("common_medium")}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-risk-low" />
            <span className="text-[10px] text-slate-500 dark:text-slate-400">{riskCounts.low} {t("common_low")}</span>
          </div>
        </div>
      </div>

      {sorted.length === 0 ? (
        <div className="h-32 flex items-center justify-center text-xs text-slate-400 dark:text-slate-600">
          No district data available
        </div>
      ) : (
        <div className="overflow-y-auto max-h-[340px]">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
                <th className="px-5 py-2.5 text-left text-[10px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">District</th>
                <th className="px-3 py-2.5 text-left text-[10px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Region</th>
                <th className="px-3 py-2.5 text-right text-[10px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Predicted</th>
                <th className="px-5 py-2.5 text-right text-[10px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Risk</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((d) => {
                const risk = d.risk_level ?? "low";
                return (
                  <tr key={d.district_id} className="border-b border-slate-100 dark:border-slate-800/60 last:border-0 hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                    <td className="px-5 py-2.5">
                      <div className="flex items-center gap-2">
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${riskDot[risk]}`} />
                        <span className="font-medium text-slate-800 dark:text-slate-200 truncate max-w-[140px]">{d.district_name}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-slate-500 dark:text-slate-400">{d.region}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-slate-700 dark:text-slate-300">
                      {d.predicted_cases != null ? d.predicted_cases.toLocaleString() : "—"}
                    </td>
                    <td className="px-5 py-2.5 text-right">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${riskBadge[risk]}`}>
                        {risk}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Historical cases come from api.predictionDistrict(districtId).historical_cases
function CasesTrendMiniChart({
  historicalCases,
}: {
  historicalCases: DistrictPredictionDetail["historical_cases"];
}) {
  const { theme } = useTheme();

  const data = useMemo(() => {
    return [...historicalCases]
      .sort((a, b) => a.week_start.localeCompare(b.week_start))
      .slice(-12)
      .map((c) => ({ date: c.week_start.slice(5), cases: c.confirmed_cases }));
  }, [historicalCases]);

  const tooltipStyle =
    theme === "dark"
      ? { backgroundColor: "#0f172a", border: "1px solid #334155", borderRadius: "8px", fontSize: "11px", color: "#e2e8f0" }
      : { backgroundColor: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "8px", fontSize: "11px", color: "#1e293b" };
  const gridStroke = theme === "dark" ? "#1e293b" : "#e2e8f0";
  const tickFill = theme === "dark" ? "#64748b" : "#94a3b8";

  if (data.length === 0) {
    return (
      <div className="h-[120px] flex items-center justify-center text-xs text-slate-400 dark:text-slate-600">
        No historical data available
      </div>
    );
  }

  return (
    <div className="h-[120px]">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
          <XAxis dataKey="date" tick={{ fontSize: 9, fill: tickFill }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 9, fill: tickFill }} axisLine={false} tickLine={false} />
          <Tooltip contentStyle={tooltipStyle} />
          <Line type="monotone" dataKey="cases" stroke="#2dd4bf" strokeWidth={2} dot={false} activeDot={{ r: 3, fill: "#2dd4bf" }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// Recent alerts feed — driven by top_alerts from backend
function ActivityFeed({ alerts }: { alerts: DashboardSummaryResponse["top_alerts"] }) {
  const { t } = useTranslation();

  const formatTime = (ts: string) => {
    const date = new Date(ts);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${Math.floor(diffHours / 24)}d ago`;
  };

  const riskBorder: Record<string, string> = {
    high: "border-risk-high/40",
    medium: "border-amber/40",
    low: "border-risk-low/40",
  };
  const riskDot: Record<string, string> = {
    high: "bg-risk-high",
    medium: "bg-amber",
    low: "bg-risk-low",
  };

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white font-display">{t("dash_activity_feed")}</h3>
          <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">{t("dash_realtime_events")}</p>
        </div>
        <Link to="/alerts" className="text-[10px] text-teal hover:text-teal-light transition-colors flex items-center gap-1">
          {t("dash_view_all")} <i className="ri-arrow-right-line" />
        </Link>
      </div>
      <div className="px-5 py-2 max-h-[300px] overflow-y-auto">
        {alerts.length === 0 ? (
          <p className="text-xs text-slate-400 dark:text-slate-600 text-center py-8">No recent alerts</p>
        ) : (
          alerts.map((a) => {
            const risk = a.risk_level ?? "low";
            return (
              <div key={a.id} className={`flex items-start gap-3 py-3 border-b border-slate-200/50 dark:border-slate-800/50 last:border-0 border-l-2 pl-3 -ml-5 ${riskBorder[risk]}`}>
                <div className="w-7 h-7 flex items-center justify-center rounded-md bg-slate-100 dark:bg-slate-800 flex-shrink-0 mt-0.5">
                  <i className="ri-alarm-warning-line text-slate-500 dark:text-slate-400 text-xs" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-xs font-medium text-slate-700 dark:text-slate-200 truncate">{a.district_name}</span>
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${riskDot[risk]}`} />
                  </div>
                  <p className="text-[10px] text-slate-500 leading-relaxed truncate">{a.recommended_action ?? "—"}</p>
                  <p className="text-[10px] text-slate-400 dark:text-slate-600 mt-1 font-mono">{formatTime(a.created_at)}</p>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// Top districts by predicted cases from DashboardSummaryResponse.district_map
function DistrictLeaderboard({
  districtMap,
}: {
  districtMap: DashboardSummaryResponse["district_map"];
}) {
  const { t } = useTranslation();

  const topDistricts = useMemo(() => {
    return [...districtMap]
      .filter((d) => d.predicted_cases != null)
      .sort((a, b) => (b.predicted_cases ?? 0) - (a.predicted_cases ?? 0))
      .slice(0, 5);
  }, [districtMap]);

  const maxCases = topDistricts[0]?.predicted_cases ?? 1;

  const riskBarColor: Record<string, string> = {
    high: "#ef4444",
    medium: "#f59e0b",
    low: "#22c55e",
  };

  const riskTextColor: Record<string, string> = {
    high: "text-risk-high",
    medium: "text-amber",
    low: "text-risk-low",
  };

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white font-display">{t("dash_district_leaderboard")}</h3>
          <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">{t("dash_top_5_growth")}</p>
        </div>
        <Link to="/predictions" className="text-[10px] text-teal hover:text-teal-light transition-colors flex items-center gap-1">
          {t("dash_view_all")} <i className="ri-arrow-right-line" />
        </Link>
      </div>
      <div className="space-y-2.5">
        {topDistricts.map((d, i) => {
          const risk = d.risk_level ?? "low";
          return (
            <div key={d.district_id} className="flex items-center gap-3">
              <span className="w-5 text-center text-xs font-mono text-slate-400 dark:text-slate-500">{i + 1}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-slate-700 dark:text-slate-300 truncate">{d.district_name}</span>
                  <span className={`text-xs font-mono font-semibold ${riskTextColor[risk]}`}>
                    {(d.predicted_cases ?? 0).toLocaleString()}
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${Math.min(100, ((d.predicted_cases ?? 0) / maxCases) * 100)}%`,
                      backgroundColor: riskBarColor[risk],
                    }}
                  />
                </div>
                <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5">
                  {(d.predicted_cases ?? 0).toLocaleString()} {t("dash_predicted_cases")} · {d.region}
                </p>
              </div>
            </div>
          );
        })}
        {topDistricts.length === 0 && (
          <p className="text-xs text-slate-400 dark:text-slate-600 text-center py-4">No prediction data available</p>
        )}
      </div>
    </div>
  );
}

function UserWelcomeBanner() {
  const { t } = useTranslation();
  const { user } = useAuth();
  if (!user) return null;

  const hour = new Date().getHours();
  const greeting =
    hour < 12 ? t("dash_good_morning") : hour < 17 ? t("dash_good_afternoon") : t("dash_good_evening");

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60 p-5 flex items-center gap-4">
      <div className="w-11 h-11 flex items-center justify-center rounded-full bg-teal-muted/20 flex-shrink-0">
        <i className="ri-user-smile-line text-teal text-lg" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-900 dark:text-white">
          {greeting}, {user.name.split(" ").pop()}
        </p>
        <p className="text-[10px] text-slate-500 dark:text-slate-500">
          {user.organizationName} ·{" "}
          <span className={`px-1.5 py-0.5 rounded border text-[10px] font-medium ${getRoleBadgeColor(user.role)}`}>
            {user.role}
          </span>
        </p>
      </div>
      <div className="hidden sm:flex items-center gap-3 text-[10px] text-slate-500 dark:text-slate-500">
        <span className="flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-teal" />
          {t("dash_online")}
        </span>
        <span>{t("dash_last_sync")}</span>
      </div>
    </div>
  );
}

// Topography snapshot card — driven by topography_snapshot from backend
function TopographyCard({
  topo,
}: {
  topo: DashboardSummaryResponse["topography_snapshot"];
}) {
  if (!topo) {
    return (
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 p-5">
        <div className="flex items-center gap-2 mb-3">
          <i className="ri-landscape-line text-slate-400 text-sm" />
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white font-display">Terrain Analysis</h3>
        </div>
        <p className="text-xs text-slate-400 dark:text-slate-600 text-center py-4">No topography data yet — run Sync Topography</p>
      </div>
    );
  }

  const metrics = [
    { label: "Avg Slope", value: topo.avg_slope_deg != null ? `${topo.avg_slope_deg.toFixed(2)}°` : "—", icon: "ri-arrow-up-line", title: "Average terrain slope across all districts" },
    { label: "Ruggedness", value: topo.avg_ruggedness_index != null ? topo.avg_ruggedness_index.toFixed(2) : "—", icon: "ri-earth-line", title: "Terrain Ruggedness Index (TRI)" },
    { label: "Depression", value: topo.avg_depression_score != null ? `${(topo.avg_depression_score * 100).toFixed(1)}%` : "—", icon: "ri-arrow-down-line", title: "Fraction of terrain below surroundings (TPI)" },
    { label: "Ponding Risk", value: topo.avg_ponding_potential != null ? topo.avg_ponding_potential.toFixed(2) : "—", icon: "ri-water-flash-line", title: "Water pooling potential (0–1)" },
  ];

  function pondColor(score: number) {
    if (score >= 0.7) return "text-risk-high";
    if (score >= 0.4) return "text-amber";
    return "text-risk-low";
  }

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <i className="ri-landscape-line text-teal text-sm" />
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white font-display">Terrain Analysis</h3>
        </div>
        <span className="text-[10px] text-slate-400">{topo.districts_with_data} district{topo.districts_with_data !== 1 ? "s" : ""}</span>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-4">
        {metrics.map((m) => (
          <div key={m.label} title={m.title} className="rounded-lg bg-slate-50 dark:bg-slate-800/50 p-2.5">
            <div className="flex items-center gap-1.5 mb-1">
              <i className={`${m.icon} text-slate-400 text-xs`} />
              <span className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wide">{m.label}</span>
            </div>
            <span className="text-sm font-semibold font-mono text-slate-900 dark:text-white">{m.value}</span>
          </div>
        ))}
      </div>

      {topo.top_ponding_districts.length > 0 && (
        <>
          <p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">Top Ponding Risk</p>
          <div className="space-y-1.5">
            {topo.top_ponding_districts.map((d) => (
              <div key={d.district_id} className="flex items-center justify-between">
                <span className="text-xs text-slate-700 dark:text-slate-300 truncate max-w-[120px]">{d.district_name}</span>
                <div className="flex items-center gap-2">
                  <div className="w-16 h-1.5 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                    <div className="h-full rounded-full bg-blue-400" style={{ width: `${Math.round(d.ponding_potential_score * 100)}%` }} />
                  </div>
                  <span className={`text-[10px] font-mono font-semibold ${pondColor(d.ponding_potential_score)}`}>
                    {(d.ponding_potential_score * 100).toFixed(0)}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<DashboardSummaryResponse | null>(null);
  const [historicalCasesData, setHistoricalCasesData] = useState<
    DistrictPredictionDetail["historical_cases"]
  >([]);

  React.useEffect(() => {
    let cancelled = false;
    async function run() {
      try {
        const [summary, districtDetail] = await Promise.all([
          api.dashboardSummary() as Promise<DashboardSummaryResponse>,
          (api.predictionDistrict(1) as Promise<DistrictPredictionDetail>).catch(() => null),
        ]);
        if (!cancelled) {
          setData(summary);
          setHistoricalCasesData(districtDetail?.historical_cases ?? []);
        }
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const districtMap = data?.district_map ?? [];
  const weatherRecords = data?.weather_snapshot?.records ?? [];
  const topAlerts = data?.top_alerts?.slice(0, 5) ?? [];
  const topoSnapshot = data?.topography_snapshot ?? null;
  const totalPredicted = data?.kpis?.predicted_cases_30d ?? 0;
  const avgGrowthRate = data?.kpis?.growth_rate_pct ?? 0;
  const highAlertDistricts = data?.kpis?.high_alert_districts ?? 0;
  const activeInterventions = data?.kpis?.active_interventions ?? 0;

  // Live risk counts from backend district_map
  const riskCounts = useMemo(() => {
    return districtMap.reduce(
      (acc, d) => {
        if (d.risk_level === "high") acc.high++;
        else if (d.risk_level === "medium") acc.medium++;
        else acc.low++;
        return acc;
      },
      { high: 0, medium: 0, low: 0 }
    );
  }, [districtMap]);


  return (
    <div className="space-y-5 animate-fade-in">
      <UserWelcomeBanner />
      {loading && (
        <div className="min-h-[200px] flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-slate-700 border-t-teal rounded-full animate-spin" />
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-lg md:text-xl font-display font-bold text-slate-900 dark:text-white">{t("dash_title")}</h1>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{t("dash_subtitle")}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors whitespace-nowrap">
            <i className="ri-calendar-line" />
            {t("dash_last_7_days")}
          </button>
          <button className="flex items-center gap-2 px-3 py-2 rounded-lg bg-teal-muted/30 border border-teal/20 text-xs text-teal hover:bg-teal-muted/50 transition-colors whitespace-nowrap">
            <i className="ri-refresh-line" />
            {t("dash_refresh_data")}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <KPICard
          title={t("dash_predicted_cases")}
          value={totalPredicted}
          change={avgGrowthRate}
          changeLabel={t("dash_vs_last")}
          icon="ri-virus-line"
          color="teal"
          delay={0}
        />
        <KPICard
          title={t("dash_growth_rate")}
          value={avgGrowthRate}
          change={avgGrowthRate}
          changeLabel={t("dash_avg_across_districts")}
          icon="ri-arrow-up-line"
          color="amber"
          delay={100}
        />
        <KPICard
          title={t("dash_high_alert")}
          value={highAlertDistricts}
          change={2}
          changeLabel={t("dash_new_this_week")}
          icon="ri-map-pin-line"
          color="red"
          delay={200}
        />
        <KPICard
          title={t("dash_active_interventions")}
          value={activeInterventions}
          change={-1}
          changeLabel={t("dash_vs_last_week")}
          icon="ri-shield-check-line"
          color="green"
          delay={300}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <DistrictMapCard districtMap={districtMap} />

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 p-4 md:p-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-white font-display">{t("dash_national_trend")}</h3>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">{t("dash_weekly_confirmed")}</p>
                </div>
                <span className="text-[10px] px-2 py-1 rounded-full bg-teal-muted/30 text-teal border border-teal/15 font-mono">
                  {t("common_live")}
                </span>
              </div>
              <CasesTrendMiniChart historicalCases={historicalCasesData} />
            </div>

            <ActivityFeed alerts={topAlerts} />
          </div>
        </div>

        <div className="space-y-5">
          <DistrictLeaderboard districtMap={districtMap} />

          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white font-display">{t("dash_quick_alerts")}</h3>
              <Link
                to="/alerts"
                className="text-[10px] text-teal hover:text-teal-light transition-colors flex items-center gap-1"
              >
                {t("dash_view_all")} <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div>
              {topAlerts.map((a, i) => (
                <QuickAlertRow key={a.id} alert={a} delay={i * 80} />
              ))}
              {topAlerts.length === 0 && !loading && (
                <p className="text-xs text-slate-400 dark:text-slate-600 text-center py-4">No active alerts</p>
              )}
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 p-5">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white font-display">{t("dash_weather_snapshot")}</h3>
              <span className="text-[10px] text-slate-500 dark:text-slate-500">{t("dash_yaounde_central")}</span>
            </div>
            <p className="text-[10px] text-slate-500 dark:text-slate-400 mb-3">{t("dash_temp_rain")}</p>
            <WeatherMiniChart records={weatherRecords} />
          </div>

          <TopographyCard topo={topoSnapshot} />

          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 p-5">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white font-display mb-3">{t("dash_district_summary")}</h3>
            <div className="space-y-3">
              {[
                { label: t("dash_total_districts"), value: String(districtMap.length), color: "text-slate-700 dark:text-slate-300" },
                { label: t("dash_high_risk"), value: String(riskCounts.high), color: "text-risk-high" },
                { label: t("dash_medium_risk"), value: String(riskCounts.medium), color: "text-amber" },
                { label: t("dash_low_risk"), value: String(riskCounts.low), color: "text-risk-low" },
              ].map((item) => (
                <div key={item.label} className="flex items-center justify-between py-1.5 border-b border-slate-200/50 dark:border-slate-800/50 last:border-0">
                  <span className="text-xs text-slate-500 dark:text-slate-400">{item.label}</span>
                  <span className={`text-sm font-semibold font-mono ${item.color}`}>{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
